import { type Context, useContext, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { AppContext } from '../../../context/AppContext';
import { OptimiseUserDetails } from '../../../types/optimise';
import LoopSpinner from '../../../ui/LoopSpinner';
import InfoModal from '../../../ui/Modal/InfoModal';
import { StyledWarningBox } from '../../../ui/Styled';
import { calendarDaysDiff } from '../../../utils/dateUtils';

const TWELVE_MONTHS_IN_DAYS = 365;

const StyledNoticeHeader = styled.div<{ $gradient: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${({ $gradient }) => $gradient};
  border-radius: 8px 8px 0 0;
`;

const StyledNoticeHeaderIcon = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  filter: brightness(0) invert(1);
`;

const StyledNoticeHeaderText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 16px;
  font-weight: 700;
  line-height: 26px;
`;

const StyledNoticeBody = styled.div<{ $gradient: string }>`
  padding: 16px;
  border-radius: 0 0 4px 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background:
    linear-gradient(${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.white}) padding-box,
    ${({ $gradient }) => $gradient} border-box;
  border: 2px solid transparent;
  border-top: none;

  p {
    margin: 0;
  }
`;

const StyledStepsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 30px;
`;

const StyledStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledStepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.variables.alternativeGradient};
  color: ${({ theme }) => theme.colors.white};
  font-size: 20px;
  font-weight: 700;
`;

const StyledButton = styled(Button)`
  margin-bottom: 0;
`;

interface AppContextType {
  recommendedTariffs: unknown[] | null;
  loadingRecommendedTariffs: boolean;
  userDetails: OptimiseUserDetails | null;
}

const hasPersonalisedData = (userDetails: OptimiseUserDetails | null): boolean => {
  if (!userDetails?.analytics_data_range?.start_date || !userDetails?.analytics_data_range?.end_date) {
    return false;
  }
  const start = new Date(userDetails.analytics_data_range.start_date);
  const end = new Date(userDetails.analytics_data_range.end_date);
  return calendarDaysDiff(end, start) >= TWELVE_MONTHS_IN_DAYS;
};

const WelcomeContainer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { recommendedTariffs, loadingRecommendedTariffs, userDetails } = useContext(
    AppContext as Context<AppContextType>
  );
  const [showModal, setShowModal] = useState(false);

  const personalised = hasPersonalisedData(userDetails);

  const estimatedUsageModalContent = (
    <>
      <h4>How we estimate your energy use</h4>
      <p>
        When {theme.language.appName} does not have a full 12 months of your household data, we estimate your energy
        usage based on average household consumption for a home similar to yours.
      </p>
      <p>
        This means the tariff recommendations shown are based on typical usage patterns rather than your own, so they
        may not perfectly reflect your household&apos;s specific needs.
      </p>
      <p className="mb-0">
        As soon as {theme.language.appName} has enough of your data, the tariff recommender will automatically switch to
        using your actual usage to give you more personalised recommendations.
      </p>
    </>
  );

  return (
    <>
      <InfoModal show={showModal} onClose={() => setShowModal(false)} infoContent={estimatedUsageModalContent} />
      <div>
        <h3 className="pb-1">Welcome to the {theme.language.appName} tariff recommender</h3>
        <p>Not sure which time-of-use tariff is best for you? We analyse your energy data to help you decide.</p>

        <div className="mb-4">
          <StyledNoticeHeader
            $gradient={personalised ? theme.variables.successGradient : theme.variables.dangerGradient}
          >
            <StyledNoticeHeaderIcon src={theme.icons.info} alt="" />
            <StyledNoticeHeaderText>
              {personalised
                ? 'Recommended tariffs are based on your personalised usage'
                : 'Recommended tariffs are based on your estimated household usage'}
            </StyledNoticeHeaderText>
          </StyledNoticeHeader>
          <StyledNoticeBody $gradient={personalised ? theme.variables.successGradient : theme.variables.dangerGradient}>
            {personalised ? (
              <>
                <p>
                  We have over 12 months of your household data, so the tariff recommender will use your actual energy
                  usage information to recommend more personalised tariffs for you.
                </p>
                <p>
                  Your recommendations will update every month to ensure we&apos;re always recommending the most
                  beneficial tariff for your household.
                </p>
              </>
            ) : (
              <>
                <p>
                  {theme.language.appName} does not have a full 12 months of your household data, we{' '}
                  <Button variant="link" onClick={() => setShowModal(true)} className="p-0">
                    estimate your energy usage
                  </Button>{' '}
                  based on the information you have told us.
                </p>
                <p>
                  As soon as {theme.language.appName} has enough data, the tariff recommender will use your actual data
                  to recommend more personalised tariffs for you.
                </p>
              </>
            )}
          </StyledNoticeBody>
        </div>

        <h5 className="mb-0">How the tariff recommender works:</h5>
        <StyledStepsSection className="mb-4">
          <StyledStepRow>
            <StyledStepNumber>1</StyledStepNumber>
            <p className="mb-0">
              <strong>Compare smart tariffs</strong> based on your data. Note that the figures shown for each tariff do
              not yet currently include standing charge information.
            </p>
          </StyledStepRow>
          <StyledStepRow>
            <StyledStepNumber>2</StyledStepNumber>
            <p className="mb-0">
              <strong>See what you would have paid</strong> on a sample of tariff types over the past year.
            </p>
          </StyledStepRow>
          <StyledStepRow>
            <StyledStepNumber>3</StyledStepNumber>
            <p className="mb-0">
              <strong>Make an informed choice.</strong> Remember that {theme.language.appName} is a guide, not a
              switching service. You&apos;ll still need to switch your tariff yourself.
            </p>
          </StyledStepRow>
          <StyledStepRow>
            <StyledStepNumber>4</StyledStepNumber>
            <p className="mb-0">
              After you&apos;ve switched you&apos;ll need to <strong>update your tariff in Settings</strong> to keep{' '}
              {theme.language.appName} working its magic.
            </p>
          </StyledStepRow>
        </StyledStepsSection>

        {loadingRecommendedTariffs ? (
          <p className="p-0 m-0">
            <LoopSpinner />
          </p>
        ) : (
          <>
            {!!recommendedTariffs?.length ? (
              <StyledButton onClick={() => navigate('/app/tariffs/recommended')} className="w-100">
                View your recommended tariffs
              </StyledButton>
            ) : (
              <StyledWarningBox>
                <h4 className="d-flex align-items-center">
                  <img src={theme.icons.info} alt="Info" className="pr-2" /> We&apos;re working on which tariffs to
                  recommend to you.
                </h4>
                <p className="mb-3">We&apos;ll let you know when the tariffs are ready.</p>
              </StyledWarningBox>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default WelcomeContainer;
